import { canActivate, redirectLoggedInTo, redirectUnauthorizedTo } from '@fido/firebase/auth-guard';
import { Routes } from '@angular/router';
import { provideLocalStorageImpl } from 'ngxtension/inject-local-storage';
import { authGuard } from './auth/guards/auth.guard';
import { emailVerifiedGuard } from './auth/guards/email-verified.guard';
import { profileCompleteGuard } from './auth/guards/profile-complete.guard';
import { unauthenticatedGuard } from './auth/guards/unauthenticated.guard';
import { changelogGuard } from './changelog/guard';
import { provideUploadContactsFile } from './contacts/commands/upload-contacts-file';
import { provideImportBatches, provideWatchImportBatch } from './contacts/data/import-batches';
import { provideSearchContacts } from './contacts/data/search-contacts';
import { organizationGuard } from './organizations/guards/organization.guard';
import { orgRoutes } from './organizations/utils/org.routes';
import { roleGuard } from './permissions/guards/role-guard';

export const routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  {
    path: 'home',
    title: 'Home',
    loadComponent: () => import('./home/pages/home/home'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'contacts',
    title: 'Contacts',
    loadComponent: () => import('./contacts/pages/contacts-page/contacts-page'),
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      organizationGuard,
      profileCompleteGuard,
      roleGuard(['owner', 'attorney', 'staff']),
    ],
    providers: [
      provideImportBatches(),
      provideUploadContactsFile(),
      provideWatchImportBatch(),
      provideSearchContacts(),
      provideLocalStorageImpl(sessionStorage),
    ],
  },
  ...orgRoutes,
  {
    path: 'inquiries',
    title: 'Inquiries',
    loadComponent: () => import('./inquiries/pages/intakes/intakes'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'consultations',
    title: 'Consultations',
    loadComponent: () => import('./consultation/pages/consultation-calendar/consultation-calendar'),
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      organizationGuard,
      profileCompleteGuard,
      roleGuard(['owner', 'attorney', 'staff']),
    ],
  },
  {
    path: 'inquiries/:sessionId',
    title: 'Inquiry Details',
    loadComponent: () => import('./inquiries/pages/intake-detail/intake-detail'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'engagement/templates',
    title: 'Engagement Templates',
    loadComponent: () => import('./engagement/pages/engagement-templates/engagement-templates'),
    canActivate: [
      authGuard,
      emailVerifiedGuard,
      organizationGuard,
      profileCompleteGuard,
      roleGuard(['owner']),
    ],
  },
  {
    path: 'verify-email',
    title: 'Verify Email',
    loadComponent: () => import('./auth/pages/verify-email/verify-email'),
    canActivate: [authGuard],
  },
  {
    path: 'sign-in',
    title: 'Sign In',
    loadComponent: () => import('./auth/pages/sign-in/sign-in'),
    ...canActivate(() => redirectLoggedInTo(['/'])),
  },
  {
    path: 'signing-out',
    title: 'Signing Out',
    loadComponent: () => import('./auth/pages/signing-out/signing-out'),
    ...canActivate(() => redirectUnauthorizedTo(['/sign-in'])),
  },
  {
    path: 'create-account',
    title: 'Create Account',
    loadComponent: () => import('./auth/pages/create-account/create-account'),
    canActivate: [unauthenticatedGuard],
  },
  {
    path: 'reset-password',
    title: 'Reset Password',
    loadComponent: () => import('./auth/pages/reset-password/reset-password'),
    ...canActivate(() => redirectLoggedInTo(['/'])),
  },
  {
    path: 'complete-sign-in',
    title: 'Complete Sign In',
    loadComponent: () => import('./auth/pages/complete-sign-in/complete-sign-in'),
    ...canActivate(() => redirectLoggedInTo(['/'])),
  },
  {
    path: 'profile',
    title: 'Profile',
    loadComponent: () => import('./auth/pages/profile/profile'),
    canActivate: [authGuard, emailVerifiedGuard],
  },
  {
    path: 'devices',
    title: 'Devices',
    loadComponent: () => import('./devices/pages/devices/devices'),
    canActivate: [authGuard, emailVerifiedGuard],
  },
  {
    path: 'notifications',
    title: 'Notifications',
    loadComponent: () => import('./notifications/pages/notifications/notifications'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'inquiries/:sessionId',
    outlet: 'detail',
    loadComponent: () => import('./inquiries/ui/inquiry-detail-flyout/inquiry-detail-flyout'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'contacts/:contactId',
    outlet: 'detail',
    loadComponent: () => import('./contacts/ui/contact-detail-flyout/contact-detail-flyout'),
    canActivate: [authGuard, emailVerifiedGuard, organizationGuard, profileCompleteGuard],
  },
  {
    path: 'changelog',
    title: 'Change Log',
    loadComponent: () => import('./changelog/changelog'),
    canActivate: [authGuard, emailVerifiedGuard, changelogGuard],
  },
  {
    path: 'onboarding',
    title: 'Onboarding',
    loadComponent: () => import('./onboarding/pages/onboarding/onboarding'),
    canActivate: [authGuard, emailVerifiedGuard],
  },
  {
    path: '**',
    redirectTo: 'sign-in',
    pathMatch: 'full',
  },
] as const satisfies Routes;

/**
 * Extract route paths from the routes array
 */
type RoutePath = (typeof routes)[number]['path'];

/**
 * Filter out wildcard routes and get valid route segments
 */
export type AppRouteSegment = Exclude<RoutePath, '**'>;

/**
 * Type-safe route paths for navigation throughout the application.
 * Automatically derived from the routes configuration - no duplication needed!
 *
 * @example
 * const navigateTo: AppRoute = '/sign-in'; // ✓ Type-safe
 * const invalid: AppRoute = '/invalid'; // ✗ Type error
 */
export type AppRoute = '/' | `/${AppRouteSegment}`;

/**
 * Runtime set of valid route segments for validation
 */
const validRouteSegments = new Set<string>(
  routes.map((route) => route.path).filter((path) => path !== '**'),
);

/**
 * Type guard to check if a string is a valid AppRouteSegment
 */
export function isAppRouteSegment(value: string): value is AppRouteSegment {
  return validRouteSegments.has(value);
}
